import React from 'react';

import Person from '@gravity-ui/icons/Person';
import Persons from '@gravity-ui/icons/Persons';
import { RadioButton } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { useCurrentAliceApp } from 'client/hooks/useCurrentAliceApp';
import { useIsDraft } from 'client/hooks/useIsDraft';
import { SkillAccess } from 'client/model/settings';
import { Channel } from 'client/model/skill';
import * as keyset from 'i18n/SettingsLabels';

import { Field } from '../Field/Field';

import styles from './InstallableAppAccessField.module.scss';

const t = i18n(keyset);

interface Props {
    installableAppAccess: SkillAccess | undefined;
    setInstallableAppAccess?: (skillAccess: SkillAccess) => void;
    disabled?: boolean;
    channel: Channel;
}

const getDescription = (access?: SkillAccess) => {
    if (access === SkillAccess.Private) return t('description-app-access-private');
    if (access === SkillAccess.Public) return t('description-app-access-public');
    return '';
};

const mapInstallableAppAccessToIcon: Record<SkillAccess, React.ReactNode> = {
    [SkillAccess.Hidden]: '',
    [SkillAccess.Private]: <Person />,
    [SkillAccess.Public]: <Persons />,
};

export const InstallableAppAccessField: React.FC<Props> = ({
    installableAppAccess,
    setInstallableAppAccess,
    disabled,
    channel,
}) => {
    const installableApp = useCurrentAliceApp()!;
    const isDraft = useIsDraft(installableApp.channel, installableApp.id);

    const isHiddenAccess = isDraft
        ? installableApp.draft.skillAccess === SkillAccess.Hidden
        : installableApp.skillAccess === SkillAccess.Hidden;

    const commonSkillAccesses = [
        {
            type: SkillAccess.Public,
            label: t('public'),
        },
        {
            type: SkillAccess.Hidden,
            label: t('hidden'),
        },
        {
            type: SkillAccess.Private,
            label: t('private'),
        },
    ];

    const smartHomeSkillAccesses = [
        {
            type: SkillAccess.Public,
            label: t('public'),
        },
        {
            type: SkillAccess.Private,
            label: t('private'),
        },
    ];

    const options = channel === Channel.SmartHome ? smartHomeSkillAccesses : commonSkillAccesses;

    const displayedOptions = isHiddenAccess
        ? options
        : options
              .filter((access) => access.type !== SkillAccess.Hidden && access.label)
              .sort((a, b) => a.label.localeCompare(b.label));

    const handleUpdate = (value: SkillAccess) => {
        if (!setInstallableAppAccess) return;
        setInstallableAppAccess(value);
    };

    return (
        <Field fieldTitle={t('skill-access')} fieldDescription={getDescription(installableAppAccess)}>
            <RadioButton
                className={styles.installableAppAccessRadioButtonsContainer}
                disabled={disabled}
                options={displayedOptions.map(({ type, label }) => ({
                    value: type,
                    content: (
                        <div className={styles.installableAppAccessLabelContainer}>
                            {mapInstallableAppAccessToIcon[type]} {label}
                        </div>
                    ),
                }))}
                onUpdate={handleUpdate}
                value={installableAppAccess}
                size="xl"
            />
        </Field>
    );
};
